import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  fetchSelfTransferDetails,
  formatDateDisplay,
  formatHodUnit,
  groundLabel,
  parseApprovers,
  reasonLabel,
  resolveStatusBadge,
} from "./selfTransfer.service";

const DetailField: React.FC<{ label: string; value?: React.ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="col-md-4 col-sm-6">
    <label className="form-label text-muted small mb-1">{label}</label>
    <div className={`fw-semibold text-dark ${mono ? "font-monospace" : ""}`}>
      {value || "-"}
    </div>
  </div>
);

const SelfTransferDetail: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const stateItem = (location.state as any)?.item;
  const [details, setDetails] = useState<any>(stateItem || null);
  const [loading, setLoading] = useState(false);

  const requestId = id || stateItem?.RequestID || stateItem?.id;

  useEffect(() => {
    if (!requestId) return;
    const loadDetails = async () => {
      try {
        setLoading(true);
        const res = await fetchSelfTransferDetails(String(requestId));
        if (res.success && res.data) {
          setDetails(res.data);
        } else if (!details) {
          toast.error("Failed to load self transfer details");
        }
      } catch (err: any) {
        if (!details) {
          toast.error(err?.message || "Failed to load details");
        }
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [requestId]);

  if (loading && !details) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-danger" style={{ color: "#ab1f24" }} role="status" />
          <p className="text-muted mt-2">Loading details...</p>
        </div>
      </div>
    );
  }

  if (!details && !loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <i className="ti ti-alert-circle text-danger fs-1 mb-2 d-block" />
          <h5>No Details Available</h5>
          <p className="text-muted">The requested self transfer details could not be found.</p>
          <Link to={routes.selfTransfer.list} className="btn btn-outline-secondary mt-2">
            Back to Requests
          </Link>
        </div>
      </div>
    );
  }

  const reqId = details.RequestID || details.referenceNumber || details.id || "-";
  const ground = groundLabel(details.TransferGround);
  const reason = reasonLabel(details.ReasonForTransfer);
  const createdDate = formatDateDisplay(details.created_at || details.createOn);
  const status = details.Status || details.status || "INPROCESS";
  const badge = resolveStatusBadge(status);
  const approversList = parseApprovers(details);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header Bar */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 fw-bold">Self Transfer Request Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.selfTransfer.list}>Self Transfer</Link>
                </li>
                <li className="breadcrumb-item active text-primary">Details ({reqId})</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-3 shadow-sm border-2 fw-medium d-flex align-items-center"
              onClick={() => navigate(routes.selfTransfer.list)}
            >
              <i className="ti ti-arrow-left me-1" />
              Back to Requests
            </button>
            <button
              type="button"
              className="btn btn-outline-danger rounded-pill px-3 shadow-sm border-2 fw-medium d-flex align-items-center"
              style={{ color: "#ab1f24", borderColor: "#ab1f24" }}
              onClick={() =>
                navigate(routes.selfTransfer.approvers.replace(":id", encodeURIComponent(String(reqId))), {
                  state: { item: details },
                })
              }
            >
              <i className="ti ti-users me-1" />
              Approvers Timeline
            </button>
          </div>
        </div>

        {/* Status Banner */}
        <div
          className="card border-0 shadow-sm mb-4"
          style={{
            backgroundColor: badge.bg,
            borderLeft: `4px solid ${badge.dotColor}`,
          }}
        >
          <div className="card-body p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-semibold text-dark">Request Status:</span>
              <span className={badge.badgeClass} style={{ fontSize: "0.85rem", padding: "6px 14px" }}>
                <span
                  className="d-inline-block rounded-circle me-1"
                  style={{ width: 8, height: 8, backgroundColor: badge.dotColor }}
                />
                {badge.label}
              </span>
            </div>
            <div className="text-muted small">
              Created on: <strong className="text-dark">{createdDate}</strong>
            </div>
          </div>
        </div>

        {/* Section 1: Request Details */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 me-2"
              style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
            >
              <i className="ti ti-file-text text-danger" style={{ color: "#ab1f24" }} />
            </div>
            <h5 className="card-title mb-0 fw-bold text-dark">Request Details</h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              <DetailField label="Request ID" value={reqId} mono />
              <DetailField
                label="Employee"
                value={
                  details.EmpID
                    ? details.EmployeeName
                      ? `${details.EmployeeName} (${details.EmpID})`
                      : details.EmpID
                    : "-"
                }
              />
              <DetailField label="Transfer Ground" value={ground} />
              <DetailField label="Reason For Transfer" value={reason} />
              {(details.ReasonForTransfer === "TEM" || reason === "Temporary") &&
                Number(details.TransferPeriod) > 0 && (
                <DetailField
                  label="Transfer Period"
                  value={`${details.TransferPeriod} ${
                    String(details.YearMonthDays || "").toUpperCase().startsWith("Y")
                      ? "Year(s)"
                      : String(details.YearMonthDays || "").toUpperCase().startsWith("M")
                      ? "Month(s)"
                      : String(details.YearMonthDays || "").toUpperCase().startsWith("D")
                      ? "Day(s)"
                      : details.YearMonthDays || ""
                  }`}
                />
              )}
              <DetailField label="Category" value={details.Category || "Executive"} />
              <DetailField
                label="Division / Office"
                value={
                  details.DivisionUnitOfficeInWhichTheEmployee || details.Division || "-"
                }
              />
              <DetailField
                label="Pay Scale"
                value={details.PayScale || details.payScale || "-"}
              />
              <DetailField label="Created On" value={createdDate} />
            </div>
          </div>
        </div>

        {/* Section 2: Destination Information */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 me-2"
              style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
            >
              <i className="ti ti-map-pin text-danger" style={{ color: "#ab1f24" }} />
            </div>
            <h5 className="card-title mb-0 fw-bold text-dark">Destination Information</h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              <DetailField
                label="Name of HOD/Unit (Destination)"
                value={
                  details.NameOfHODUnit
                    ? `${formatHodUnit(details.NameOfHODUnit)} ${details.Designation ? `(${details.Designation})` : ""}`
                    : "Intra-Dept HOD"
                }
              />
              {details.TransferUnder && (
                <DetailField
                  label="Transfer Under"
                  value={
                    details.TransferUnderDesignation
                      ? `${details.TransferUnder} (${details.TransferUnderDesignation})`
                      : details.TransferUnder
                  }
                />
              )}
              {details.Department && (
                <DetailField label="Department" value={details.Department} />
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Remarks */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 me-2"
              style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
            >
              <i className="ti ti-message-circle text-danger" style={{ color: "#ab1f24" }} />
            </div>
            <h5 className="card-title mb-0 fw-bold text-dark">Remarks</h5>
          </div>
          <div className="card-body p-4">
            <div>
              <label className="form-label text-muted small mb-1 fw-semibold">Employee Remark</label>
              <div className="p-3 bg-light rounded-3 text-dark">
                {details.Remarks || details.comment || "No remarks provided"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfTransferDetail;
